import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import storage from '@dashboard/services/storage';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import Button from '@dashboard/components/Button';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderActions from '@dashboard/components/CardHeaderActions';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CheckoutListItem from '@dashboard/components/CheckoutListItem';
import EmptyState from '@dashboard/components/EmptyState';
import EventListItem from '@dashboard/components/EventListItem';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import FormListItem from '@dashboard/components/FormListItem';
import Spinner from '@dashboard/components/Spinner';
import { sellerPagesKeyWithFilter } from '@/dashboard/lib/queryKeys';

const FILTER_TYPES = [
  {
    name: 'Checkouts',
    value: 'checkout',
  },
  {
    name: 'Events',
    value: 'event',
  },
  {
    name: 'Forms',
    value: 'form',
  },
];

const FILTER_OPTIONS = {
  PUBLISHED: {
    value: 'published',
    label: 'Active',
  },
  ARCHIVED: {
    value: 'archived',
    label: 'Archived',
  },
};

function getStatusFromQuery(status) {
  const statusOption = Object.keys(FILTER_OPTIONS).find(
    key => FILTER_OPTIONS[key].value === status,
  );

  return statusOption || Object.keys(FILTER_OPTIONS)[0];
}

function getTypes(query) {
  const storedTypes = storage.local.getItem('pages-types');

  if (storedTypes) {
    return JSON.parse(storedTypes);
  }

  return query.types || [];
}

const Pages = () => {
  const query = useUrlQuery();
  const history = useHistory();

  const [search, setSearch] = useState(query.search || '');
  const [status, setStatus] = useState(getStatusFromQuery(query.status));
  const [types, setTypes] = useState(getTypes(query));
  const [shouldUpdateTypesFromStorage, setShouldUpdateTypesFromStorage] = useState(true);

  useEffect(() => {
    if (shouldUpdateTypesFromStorage) {
      const storedTypes = storage.local.getItem('pages-types');

      if (types.length === 0 && storedTypes) {
        const parsedTypes = JSON.parse(storedTypes);

        if (parsedTypes.length > 0) {
          console.log(parsedTypes);
        }
      } else {
        storage.local.setItem('pages-types', JSON.stringify(types));
      }
    } else {
      storage.local.setItem('pages-types', JSON.stringify(types));
      setShouldUpdateTypesFromStorage(true); // Reset the flag after updating
    }
  }, [types, shouldUpdateTypesFromStorage]);

  useEffect(() => {
    setSearch(query.search || '');
  }, [query.search]);

  useEffect(() => {
    setStatus(getStatusFromQuery(query.status));
  }, [query.status]);

  const filter = {
    type: types,
    status: FILTER_OPTIONS[status].value,
    search,
  };

  useEffect(() => {
    history.replace({
      search: stringify({
        ...query,
        ...(status && { status: FILTER_OPTIONS[status].value }),
        ...(search && { search }),
      }),
    });
  }, [status, search]);

  return (
    <>
      <Card variant="root">
        <CardHeader variant="root">
          <CardHeaderTitle variant="root" className="w-full">
            Pages
          </CardHeaderTitle>

          <div className="w-full flex flex-col lg:flex-row gap-x-4 gap-y-2 lg:items-end lg:justify-between">
            <CardHeaderActions>
              <div className="flex flex-wrap gap-2 items-center">
                {FILTER_TYPES.map(type => (
                  <button
                    key={type.name}
                    className={clsx('px-2 py-1 rounded-full border', {
                      'border-gray-200 text-gray-600 hover:bg-gray-50': !types.includes(type.value),
                      'border-chocolate-1000 text-chocolate-600 hover:bg-chocolate-50':
                        types.includes(type.value),
                    })}
                    onClick={() => {
                      const newTypes = types.includes(type.value)
                        ? types.filter(t => t !== type.value)
                        : [...types, type.value];
                      setTypes(newTypes);
                      setShouldUpdateTypesFromStorage(false); // Prevent updating from storage
                    }}
                  >
                    {type.name}
                  </button>
                ))}
              </div>
            </CardHeaderActions>

            <CardHeaderActions>
              <SearchInput
                onChange={setSearch}
                initialValue={search}
                name="search_pages"
                placeholder="Search pages"
              />

              <FilterInput options={FILTER_OPTIONS} onChange={setStatus} value={status} />

              <Button to={`/pages/new`} appearance="primary" wide>
                Create page
                <ArrowRightIcon className="w-4 h-4 ml-2 align-baseline" />
              </Button>
            </CardHeaderActions>
          </div>
        </CardHeader>

        <PagesList filter={filter} />
      </Card>
    </>
  );
};

function PagesList({ filter = {} }) {
  const { sellerId } = useAuth();
  const { getPages } = usePageQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading, hasNextPage } =
    useInfiniteQuery({
      queryKey: sellerPagesKeyWithFilter(sellerId, filter),
      queryFn: async ({ pageParam }) =>
        await getPages(sellerId, {
          ...filter,
          sort: JSON.stringify({
            createdAt: -1,
          }),
          limit: 10,
          skip: pageParam * 10,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;

        return allPages.length;
      },
    });

  if (isLoading) {
    return (
      <CardContent variant="root">
        <Spinner />
      </CardContent>
    );
  }

  if (isError) {
    return (
      <CardContent variant="root">
        Failed to show pages. Please refresh or contact support.
      </CardContent>
    );
  }

  const hasPages = data?.pages?.[0]?.data?.length > 0;

  if (!hasPages && filter.search) {
    return (
      <CardContent variant="root">
        <EmptyState>
          <EmptyState.Title>No pages found</EmptyState.Title>
          <EmptyState.Subtitle>Try searching for something else.</EmptyState.Subtitle>
        </EmptyState>
      </CardContent>
    );
  }

  if (!hasPages && filter.status !== FILTER_OPTIONS['PUBLISHED'].value) {
    return (
      <CardContent variant="root">
        <EmptyState>
          <EmptyState.Title>No pages found</EmptyState.Title>
          <EmptyState.Subtitle>Adjust filters to see more pages.</EmptyState.Subtitle>
        </EmptyState>
      </CardContent>
    );
  }

  if (!hasPages) {
    return (
      <CardContent variant="root">
        <EmptyState>
          <EmptyState.Title>Create your first page</EmptyState.Title>
          <EmptyState.Subtitle>
            Sell products, event tickets, subscriptions, and more.
          </EmptyState.Subtitle>
          <EmptyState.Action to="/pages/new" wide>
            Create page
          </EmptyState.Action>
        </EmptyState>
      </CardContent>
    );
  }

  const showPageTypeLabel = filter.type.length === 0 || filter.type.length > 1;

  return (
    <CardContent className="mb-20">
      <BottomScrollListener
        onBottom={() => {
          if (!hasNextPage) return;

          fetchNextPage();
        }}
      >
        {data?.pages?.map(data =>
          data?.data.map(page => {
            if (page.type === 'checkout') {
              return (
                <CheckoutListItem
                  key={page._id}
                  checkout={page}
                  isMainView
                  showPageTypeLabel={showPageTypeLabel}
                />
              );
            }
            if (page.type === 'event') {
              return (
                <EventListItem
                  key={page._id}
                  page={page}
                  isMainView
                  showPageTypeLabel={showPageTypeLabel}
                />
              );
            }
            if (page.type === 'form') {
              return (
                <FormListItem
                  key={page._id}
                  page={page}
                  isMainView
                  showPageTypeLabel={showPageTypeLabel}
                />
              );
            }
          }),
        )}
      </BottomScrollListener>

      {isFetchingNextPage && <Spinner className="m-4" />}
    </CardContent>
  );
}

export default Pages;
